import React, {
  useContext,
  useEffect,
  useState,
  useMemo,
  type ReactElement,
} from "react";

import { Box } from "@mui/system";
import {
  Button,
  Popper,
  Paper,
  InputAdornment,
  IconButton,
  Typography,
  TextField,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
} from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import DeleteIcon from "@mui/icons-material/Delete";
import { ExpandMore, ExpandLess } from "@mui/icons-material";
import { debounce } from "@mui/material/utils";

import { useFetch } from "../services/hooks/useFetch";
import { ProjectContext } from "../contexts/ProjectContext";
import { apiPrefix } from "../utils/main";

export default function SnippetsEnabledTextField({
  reasonText = "",
  labelText = "Enter Note/Reason",
  disabled = false,
  attributeTypeId,
  snippetType,
  textChanged,
  snippetDialogWidthPx = 400,
  snippetDialogHeightPx = 200,
}: Readonly<{
  reasonText?: string;
  labelText?: string;
  disabled?: boolean;
  attributeTypeId: string | null;
  snippetType: string;
  textChanged: (value: string) => void;
  snippetDialogWidthPx?: number;
  snippetDialogHeightPx?: number;
}>): {
  snippetsEnabledTextField: ReactElement<
    React.JSX.IntrinsicElements["div"],
    string
  >;
  resetReasonField: () => void;
} {
  const { id: project_id } = useContext(ProjectContext) || { id: "" };

  //
  // Show/Hide Snippets Dialog
  //
  const [snippetsDialogIsShown, setSnippetsDialogIsShown] = useState(false);
  let currentIcon = snippetsDialogIsShown ? <ExpandLess /> : <ExpandMore />;
  const [showText, setShowText] = useState("Show Snippets");
  const [anchorElement, setAnchorElement] = useState<null | HTMLElement>(null);

  const toggleShown = (event: React.MouseEvent<HTMLButtonElement>) => {
    setSnippetsDialogIsShown(!snippetsDialogIsShown);
    setShowText(snippetsDialogIsShown ? "Show Snippets" : "Hide Snippets");
    setAnchorElement(event.currentTarget);
    currentIcon = snippetsDialogIsShown ? <ExpandLess /> : <ExpandMore />;
  };

  //
  // Snippet Load and Create
  //
  // We need both a full download of the snippets for this dialog
  // AND a search results list.
  //
  const {
    isLoading: fullIsLoading,
    data: fullSnippets = [],
    fetch: getAllSnippets,
  } = useFetch<Snippet[]>();
  const [forceFullReload, setForceFullReload] = useState(0);

  const {
    data: searchResults = [],
    delete: deleteSnippet,
    fetch: getSearchResults,
  } = useFetch<Snippet[]>();

  const { create: createSnippet } = useFetch<Snippet, CreatableSnippet>();

  const [deleteId, setDeleteId] = useState<string>("");

  const deleteSnippetById = (id: string) => {
    deleteSnippet({
      path: `${apiPrefix}/projects/${project_id}/snippet/${id}`,
    }).then(() => setForceFullReload(forceFullReload + 1));
  };

  // We need to load the full snippets set when the dialog loads so that we can
  // do the logic to enable/disable the "Save Snippet" button.  So we DO NOT
  // check the 'snippetsDialogIsShown' here.
  useEffect(() => {
    if (!disabled) {
      getAllSnippets(
        `${apiPrefix}/projects/${project_id}/snippet?` +
          `type=${snippetType}` +
          `&attributeTypeId=${attributeTypeId || ""}` +
          `&search=`,
      );
    }
  }, [
    disabled,
    project_id,
    snippetType,
    getAllSnippets,
    forceFullReload,
    attributeTypeId,
  ]);

  //
  // Text Field and Saving of current text as Snippet
  //
  const [textfieldValue, setTextfieldValue] = useState(reasonText);
  const [isSavable, setIsSavable] = useState(false);

  const resetReasonField = function () {
    setTextfieldValue("");
    textChanged("");
  };

  // Determine if we can save this snippet
  function textfieldChanged(event: React.ChangeEvent<HTMLInputElement>) {
    setTextfieldValue(event.target.value);
    const newValue = event.target.value.trim().toLowerCase();
    if (newValue !== "" && !fullIsLoading) {
      const matches = fullSnippets.filter(
        (s) => s.value.trim().toLowerCase() === newValue,
      );
      if (snippetType !== "ATTRIBUTE" || attributeTypeId)
        setIsSavable(matches.length === 0);
    }
    textChanged(event.target.value);
  }

  const saveCurrentValueAsSnippet = function () {
    if (!isSavable) {
      return;
    }
    createSnippet({
      path: `${apiPrefix}/projects/${project_id}/snippet`,
      body: {
        type: snippetType,
        value: textfieldValue.trim(),
        attributeTypeId: attributeTypeId,
      },
      postResponseAction: (data) => {
        if (data) {
          fullSnippets.unshift(data);
          if (textMatches(data.value, searchText)) {
            searchResults.unshift(data);
          }
        }
      },
    });
    setIsSavable(false);
  };

  //
  // Search
  //
  const [searchText, setCurrentSearch] = useState("");

  function searchChanged(event: React.ChangeEvent<HTMLInputElement>) {
    const searchTerm = event.target.value;
    setCurrentSearch(searchTerm);
    if (snippetsDialogIsShown && searchTerm.trim() !== "") {
      debouncedSearch(searchTerm);
    }
  }

  const debouncedSearch = useMemo(
    () =>
      debounce((searchTerm: string) => {
        void getSearchResults(
          `${apiPrefix}/projects/${project_id}/snippet?` +
            `type=${snippetType}` +
            `&attributeTypeId=${attributeTypeId || ""}` +
            `&search=${searchTerm}`,
        );
      }, 500),
    [getSearchResults, project_id, snippetType, attributeTypeId],
  );

  // That little "x" at the end of the search field
  const [showClearIcon, setShowClearIcon] = useState(false);
  useEffect(() => {
    setShowClearIcon(searchText.length > 0);
  }, [searchText]);

  function textMatches(text: string, searchValue: string): boolean {
    if (!text || !searchValue || searchValue === "") {
      return false;
    }
    const escapedTextToBold = searchValue.replace(
      /[.*+?^${}()|[\]\\]/g,
      "\\$&",
    );
    const regex = new RegExp(`(${escapedTextToBold})`, "gi");
    return !!text?.match(regex)?.length;
  }

  // This is from Google's AI:
  const boldedSnippet = (text: string, textToBold: string): React.ReactNode => {
    if (!textToBold) {
      return text;
    }
    const escapedTextToBold = textToBold.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const regex = new RegExp(`(${escapedTextToBold})`, "gi");
    return text.split(regex).map((part, index) => {
      if (part.length > 0 && part.toLowerCase() === textToBold.toLowerCase()) {
        return <strong key={index}>{part}</strong>;
      }
      // This is a non-match, so return the plain string
      return part;
    });
  };

  return {
    resetReasonField: resetReasonField,
    snippetsEnabledTextField: (
      <div className="snippets-div">
        <TextField
          size={"small"}
          fullWidth
          className="snippets-textfield"
          label={labelText}
          onChange={textfieldChanged}
          placeholder={labelText}
          multiline
          disabled={disabled}
          rows={2}
          required
          value={textfieldValue}
          sx={{ paddingBottom: "4px" }}
        />
        <Box
          className="button-box"
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "top",
            width: "100%",
          }}
        >
          <Button
            size="small"
            variant="contained"
            sx={{ textTransform: "none" }}
            id="show-snippet-button"
            disabled={disabled}
            onClick={toggleShown}
            endIcon={currentIcon}
          >
            {showText}
          </Button>
          <Button
            size="small"
            variant="contained"
            sx={{ textTransform: "none" }}
            id="save-as-snippet"
            disabled={!isSavable}
            onClick={saveCurrentValueAsSnippet}
          >
            Save as Snippet
          </Button>
        </Box>
        {snippetsDialogIsShown && (
          <Popper
            id={"snippets-popper"}
            style={{ zIndex: 1500 }}
            sx={{
              width: snippetDialogWidthPx + "px",
              height: "fit-content",
              padding: "8px",
            }}
            open={snippetsDialogIsShown}
            anchorEl={anchorElement}
            placement={"bottom-start"}
            disablePortal={false}
            modifiers={[
              {
                name: "flip",
                enabled: true,
                options: {
                  altBoundary: false,
                  rootBoundary: "viewport",
                  padding: 8,
                },
              },
              {
                name: "preventOverflow",
                enabled: true,
                options: {
                  altAxis: true,
                  altBoundary: false,
                  tether: false,
                  rootBoundary: "viewport",
                  padding: 8,
                },
              },
              {
                name: "arrow",
                enabled: false,
              },
            ]}
          >
            <Paper elevation={8} sx={{ p: 2 }}>
              <TextField
                id={"snippets-search-input"}
                variant={"outlined"}
                className="snippets-search-input"
                label="Search snippets"
                fullWidth={true}
                onChange={searchChanged}
                value={searchText}
                size={"small"}
                slotProps={{
                  input: {
                    endAdornment: showClearIcon && (
                      <InputAdornment
                        position={"end"}
                        sx={{ marginRight: "1px" }}
                      >
                        <IconButton
                          onClick={() => {
                            setCurrentSearch("");
                          }}
                        >
                          <ClearIcon />
                        </IconButton>
                      </InputAdornment>
                    ),
                  },
                }}
              />
              {fullIsLoading ? (
                <Box
                  sx={{
                    display: "flex",
                    width: "100%",
                    justifyContent: "center",
                  }}
                >
                  <Typography>Snippets are loading.</Typography>
                </Box>
              ) : fullSnippets.length === 0 ? (
                <Box
                  sx={{
                    display: "flex",
                    width: "100%",
                    justifyContent: "center",
                  }}
                >
                  <Typography>No snippets have been saved yet.</Typography>
                </Box>
              ) : (
                <Box
                  sx={{
                    display: "flex",
                    width: "100%",
                    maxHeight: snippetDialogHeightPx - 70 + "px",
                  }}
                >
                  <List
                    className={"snippets-list"}
                    dense
                    sx={{ overflowY: "scroll", width: "100%" }}
                  >
                    {(searchText === "" ? fullSnippets : searchResults).map(
                      (snippet) => (
                        <ListItem
                          sx={{ width: "100%" }}
                          key={snippet.id}
                          disablePadding
                          secondaryAction={
                            deleteId === snippet.id ? (
                              <span>
                                <Button
                                  size={"small"}
                                  variant={"outlined"}
                                  sx={{ marginRight: "0.5em" }}
                                  onClick={() => {
                                    if (deleteId === snippet.id)
                                      setDeleteId("");
                                  }}
                                >
                                  Cancel
                                </Button>
                                <Button
                                  size={"small"}
                                  sx={{
                                    backgroundColor: "red",
                                    color: "white",
                                  }}
                                  onClick={() => {
                                    deleteSnippetById(snippet.id);
                                    setDeleteId("");
                                  }}
                                >
                                  DELETE THIS SNIPPET
                                </Button>
                              </span>
                            ) : (
                              <IconButton
                                edge="end"
                                onClick={() => {
                                  setDeleteId(snippet.id);
                                }}
                              >
                                <DeleteIcon />
                              </IconButton>
                            )
                          }
                        >
                          <ListItemButton
                            className={"snippet-list-item"}
                            sx={{
                              padding: 0,
                              marginRight:
                                deleteId === snippet.id ? "14.5em" : "3em",
                            }}
                            onClick={() => {
                              setTextfieldValue(snippet.value);
                              textChanged(snippet.value);
                              setSnippetsDialogIsShown(false);
                            }}
                          >
                            <ListItemText
                              primary={
                                <>
                                  {" "}
                                  {boldedSnippet(
                                    snippet.value,
                                    searchText,
                                  )}{" "}
                                </>
                              }
                            />
                          </ListItemButton>
                        </ListItem>
                      ),
                    )}
                  </List>
                </Box>
              )}
            </Paper>
          </Popper>
        )}
      </div>
    ),
  };
}
